import { useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import type { CompanySuggestion } from '@expeditor/shared';
import { lookupApi } from '../api/integrations';

interface Props {
  onSelect: (suggestion: CompanySuggestion) => void;
}

/**
 * Подсказки реквизитов от DaData. Ввод по ИНН / ОГРН / названию,
 * через 400мс debounce шлёт POST /lookup/company. Кликом — onSelect.
 *
 * Если DaData отключена/не настроена — backend возвращает [], и блок
 * показывает «Подсказки не настроены» вместо результатов.
 */
export function CompanyLookup({ onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<CompanySuggestion[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setItems(null);
      setError(null);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      lookupApi
        .company(query.trim())
        .then((r) => setItems(r.suggestions))
        .catch((err) =>
          setError(err instanceof Error ? err.message : 'Не удалось получить подсказки'),
        )
        .finally(() => setLoading(false));
    }, 400);
  }, [query]);

  const reset = () => {
    setQuery('');
    setItems(null);
  };

  const typeLabel = (t: CompanySuggestion['type']) => {
    switch (t) {
      case 'ENTREPRENEUR':
        return 'ИП';
      case 'LEGAL_RU':
        return 'ЮЛ РФ';
      case 'LEGAL_FOREIGN':
        return 'Иностр.';
    }
  };

  return (
    <Card withBorder padding="md" radius="md" bg="gray.0">
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Быстрое заполнение по ИНН / ОГРН / названию
        </Text>
        <TextInput
          placeholder="Например: 7707083893 или Сбербанк"
          leftSection={<IconSearch size={14} />}
          rightSection={
            query ? (
              <Tooltip label="Очистить">
                <ActionIcon variant="subtle" onClick={reset}>
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            ) : null
          }
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          autoComplete="off"
        />

        {loading && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="xs" c="dimmed">
              Ищу...
            </Text>
          </Group>
        )}

        {error && (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}

        {items !== null && !loading && items.length === 0 && (
          <Text size="xs" c="dimmed">
            Ничего не найдено. Проверьте ввод или заполните форму вручную ниже.
            <br />
            (Если подсказки никогда не работают — обратитесь к администратору,
            ему нужно настроить DaData в разделе «Интеграции».)
          </Text>
        )}

        {items !== null && items.length > 0 && (
          <ScrollArea h={280} type="auto">
            <Stack gap={4}>
              {items.map((s, i) => (
                <Card
                  key={`${s.inn ?? ''}-${i}`}
                  withBorder
                  padding="xs"
                  radius="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    onSelect(s);
                    reset();
                  }}
                >
                  <Group justify="space-between" wrap="nowrap" gap="xs">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} truncate>
                        {s.label}
                      </Text>
                      <Text size="xs" c="dimmed">
                        ИНН {s.inn ?? '—'}
                        {s.kpp ? ` · КПП ${s.kpp}` : ''}
                        {s.ogrn ? ` · ОГРН ${s.ogrn}` : ''}
                      </Text>
                      {s.legalAddress && (
                        <Text size="xs" c="dimmed" truncate>
                          {s.legalAddress}
                        </Text>
                      )}
                    </div>
                    <Badge color="blue" variant="light" size="sm">
                      {typeLabel(s.type)}
                    </Badge>
                  </Group>
                </Card>
              ))}
            </Stack>
          </ScrollArea>
        )}

        {items !== null && items.length > 0 && (
          <Group justify="flex-end">
            <Button size="xs" variant="subtle" onClick={reset}>
              Закрыть подсказки
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
